import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { decodeJwtPayload } from '@clinic-platform/shared-types';

import { APP_CONFIG } from '../config/app-config.token';
import { AppConfig } from '../config/app-config.interface';
import { TokenStorageService } from '../auth/token-storage.service';
import { SystemAdminContextService } from '../context/system-admin-context.service';

@Injectable({
  providedIn: 'root'
})
export class ApiClientService
{
  private readonly http = inject(HttpClient);
  private readonly config = inject<AppConfig>(APP_CONFIG);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly missingSystemAdminCompanySentinel = '__missing_context__';

  public get<TResponse>(
    path: string,
    query?: Record<string, string | number | boolean | null | undefined>,
    headers?: Record<string, string>
  ): Observable<TResponse>
  {
    return this.http.get<TResponse>(this.buildUrl(path), {
      params: this.buildHttpParams(path, query),
      headers: this.buildHttpHeaders(headers)
    });
  }

  public post<TResponse, TBody = unknown>(
    path: string,
    body?: TBody,
    query?: Record<string, string | number | boolean | null | undefined>,
    headers?: Record<string, string>
  ): Observable<TResponse>
  {
    return this.http.post<TResponse>(this.buildUrl(path), body, {
      params: this.buildHttpParams(path, query),
      headers: this.buildHttpHeaders(headers)
    });
  }

  public patch<TResponse, TBody = unknown>(
    path: string,
    body?: TBody,
    query?: Record<string, string | number | boolean | null | undefined>,
    headers?: Record<string, string>
  ): Observable<TResponse>
  {
    return this.http.patch<TResponse>(this.buildUrl(path), body, {
      params: this.buildHttpParams(path, query),
      headers: this.buildHttpHeaders(headers)
    });
  }

  public delete<TResponse>(
    path: string,
    query?: Record<string, string | number | boolean | null | undefined>,
    headers?: Record<string, string>
  ): Observable<TResponse>
  {
    return this.http.delete<TResponse>(this.buildUrl(path), {
      params: this.buildHttpParams(path, query),
      headers: this.buildHttpHeaders(headers)
    });
  }

  private buildUrl(path: string): string
  {
    const normalizedBaseUrl = this.config.apiBaseUrl.replace(/\/+$/, '');
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;

    return `${normalizedBaseUrl}${normalizedPath}`;
  }

  private buildHttpParams(
    path: string,
    query?: Record<string, string | number | boolean | null | undefined>
  ): HttpParams
  {
    const normalizedQuery = { ...(query ?? {}) };
    const currentRole = this.getCurrentRoleFromToken();
    const selectedCompanyId = this.systemAdminContext.getCompanyId();
    const isGlobalScope = this.systemAdminContext.isGlobalScope();

    if (
      currentRole === 'system_admin'
      && !isGlobalScope
      && !normalizedQuery['companyId']
      && this.shouldInjectSystemAdminCompanyId(path)
    ) {
      normalizedQuery['companyId'] = selectedCompanyId
        ? selectedCompanyId
        : this.missingSystemAdminCompanySentinel;
    }

    let params = new HttpParams();
    for (const [key, value] of Object.entries(normalizedQuery))
    {
      if (value !== undefined && value !== null && value !== '')
      {
        params = params.set(key, String(value));
      }
    }

    return params;
  }

  private shouldInjectSystemAdminCompanyId(path: string): boolean
  {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const scopedPrefixes = [
      '/appointments',
      '/slots',
      '/devices',
      '/device-types',
      '/procedure-types',
      '/clients',
      '/users',
      '/clinics',
      '/company-settings',
      '/connectors',
      '/device-downtime',
      '/specialist-downtime',
      '/reschedule-proposals',
      '/notification-queue',
    ];

    return scopedPrefixes.some((prefix) => normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`));
  }

  private getCurrentRoleFromToken(): string | null
  {
    const token = this.tokenStorage.getAccessToken();
    if (!token)
    {
      return null;
    }

    const payload = decodeJwtPayload<{ role?: string }>(token);
    return payload?.role ?? null;
  }

  private buildHttpHeaders(
    headers?: Record<string, string>
  ): HttpHeaders | undefined
  {
    if (!headers)
    {
      return undefined;
    }

    let httpHeaders = new HttpHeaders();

    for (const [key, value] of Object.entries(headers))
    {
      httpHeaders = httpHeaders.set(key, value);
    }

    return httpHeaders;
  }
}
